import {
  Button,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useRef, useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';

import type { AppError } from '../../api/contract';
import type { DataSource } from '../../config/capabilities';
import { useRepository } from '../../data/RepositoryProvider';
import { useResource } from '../../hooks/useResource';
import { paths } from '../../routes/paths';
import { DataSourceBadge } from '../../shared/data/DataSourceBadge';
import { PageState } from '../../shared/data/PageState';
import { countActiveFilterValues } from '../../shared/filter/activeFilterCount';
import { ListFilterPanel } from '../../shared/filter/ListFilterPanel';
import { formatTimestamp, type TimeLanguage } from '../../shared/format/time';
import { PageHeader } from '../../shared/layout/PageHeader';
import { bangkokWallTimeToRfc3339 } from '../commission-ledger/types';
import { auditRepositoryToken, defaultAuditLogQuery } from './repository';
import type { AuditLog, AuditLogFilters, AuditLogQuery, AuditLogRole } from './types';

const roleOptions: readonly AuditLogRole[] = [
  'SUPER_ADMIN',
  'AGENT_SUPER_USER',
  'AGENT_ADMIN_USER',
];

export function AuditLogPage({ dataSource }: { readonly dataSource: DataSource }) {
  const repository = useRepository(auditRepositoryToken);
  const [searchParams, setSearchParams] = useSearchParams();
  const { i18n, t } = useTranslation();
  const language: TimeLanguage = i18n.language.startsWith('th') ? 'th' : 'en';
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('md'));
  const [query, setQuery] = useState<AuditLogQuery>(() => initialQuery(searchParams));
  const [history, setHistory] = useState<readonly string[]>(['']);
  const [page, setPage] = useState(0);
  const [refresh, setRefresh] = useState(0);
  const resource = useResource(
    (signal) => repository.listAuditLogs(query, signal),
    [query, refresh, repository],
    (value) => value.items.length === 0,
  );
  const reset = (filters: AuditLogFilters) => {
    setHistory(['']);
    setPage(0);
    setSearchParams({}, { replace: true });
    setQuery({ ...query, cursor: '', filters });
  };
  return (
    <Stack spacing={2} sx={{ minWidth: 0 }}>
      <PageHeader
        actions={<DataSourceBadge source={dataSource} />}
        subtitle={t('audit.subtitle')}
        title={t('audit.title')}
      />
      <AuditFilters onApply={reset} value={query.filters} />
      <PageState
        emptyMessage={t('audit.empty')}
        error={toAuditPageError(resource.error, t('audit.errors.loadList'))}
        onRetry={() => setRefresh((value) => value + 1)}
        status={resource.status}
      >
        {resource.data === null ? null : (
          <>
            {mobile ? (
              <CompactAuditResults items={resource.data.items} language={language} />
            ) : (
              <TableRegion label={t('audit.table')}>
                <table aria-label={t('audit.table')}>
                  <thead>
                    <tr>
                      <th>{t('audit.fields.id')}</th>
                      <th>{t('audit.fields.action')}</th>
                      <th>{t('audit.fields.entity')}</th>
                      <th>{t('audit.fields.actor')}</th>
                      <th>{t('audit.fields.partner')}</th>
                      <th>{t('audit.fields.occurredAt')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {resource.data.items.map((item) => (
                      <tr key={item.id}>
                        <td>
                          <Link to={`${paths.audit}/${encodeURIComponent(item.id)}`}>
                            {item.id}
                          </Link>
                        </td>
                        <td>{item.action}</td>
                        <td>
                          {item.entityType} · {item.entityId ?? t('common.notAvailable')}
                        </td>
                        <td>
                          {item.principalActorId} · {item.principalRole ?? t('common.notAvailable')}
                        </td>
                        <td>{item.partnerId ?? t('audit.allPartners')}</td>
                        <td>
                          {formatTimestamp(item.occurredAt, 'Asia/Bangkok', language).display}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableRegion>
            )}
            {resource.data.requestId === null ? null : (
              <Typography variant="caption">
                {t('audit.fields.requestId')}: {resource.data.requestId}
              </Typography>
            )}
            <CursorActions
              hasNext={resource.data.nextCursor !== null}
              page={page}
              onNext={() => {
                const cursor = resource.data?.nextCursor;
                if (cursor === null || cursor === undefined) return;
                const index = page + 1;
                setHistory((current) => [...current.slice(0, index), cursor]);
                setPage(index);
                setQuery((current) => ({ ...current, cursor }));
              }}
              onPrevious={() => {
                const index = page - 1;
                setPage(index);
                setQuery((current) => ({ ...current, cursor: history[index] ?? '' }));
              }}
            />
          </>
        )}
      </PageState>
    </Stack>
  );
}

function AuditFilters({
  onApply,
  value,
}: {
  readonly onApply: (filters: AuditLogFilters) => void;
  readonly value: AuditLogFilters;
}) {
  const initialValue = useRef(value);
  const { register, handleSubmit, reset } = useForm<AuditLogFilters>({
    defaultValues: initialValue.current,
  });
  const { t } = useTranslation();
  const activeSecondaryFilterCount = countActiveFilterValues(
    {
      principalRole: value.principalRole,
      partnerId: value.partnerId,
      agentId: value.agentId,
      requestId: value.requestId,
      sourceIp: value.sourceIp,
      entityId: value.entityId,
      to: value.to,
    },
    {
      principalRole: defaultAuditLogQuery.filters.principalRole,
      partnerId: defaultAuditLogQuery.filters.partnerId,
      agentId: defaultAuditLogQuery.filters.agentId,
      requestId: defaultAuditLogQuery.filters.requestId,
      sourceIp: defaultAuditLogQuery.filters.sourceIp,
      entityId: defaultAuditLogQuery.filters.entityId,
      to: defaultAuditLogQuery.filters.to,
    },
  );
  return (
    <ListFilterPanel
      activeSecondaryFilterCount={activeSecondaryFilterCount}
      ariaLabel={t('filterPanel.label', { page: t('audit.title') })}
      onClear={() => {
        reset(defaultAuditLogQuery.filters);
        onApply(defaultAuditLogQuery.filters);
      }}
      onSubmit={handleSubmit(onApply)}
      primaryFields={
        <>
          <TextField
            label={t('audit.fields.principalActorId')}
            size="small"
            {...register('principalActorId')}
          />
          <TextField label={t('audit.fields.action')} size="small" {...register('action')} />
          <TextField
            label={t('audit.fields.entityType')}
            size="small"
            {...register('entityType')}
          />
          <TextField
            label={t('audit.fields.from')}
            size="small"
            type="datetime-local"
            {...register('from')}
          />
        </>
      }
      secondaryFields={
        <>
          <TextField
            defaultValue={initialValue.current.principalRole}
            label={t('audit.fields.principalRole')}
            select
            size="small"
            {...register('principalRole')}
          >
            <MenuItem value="">{t('common.all')}</MenuItem>
            {roleOptions.map((role) => (
              <MenuItem key={role} value={role}>
                {role}
              </MenuItem>
            ))}
          </TextField>
          <TextField label={t('audit.fields.partnerId')} size="small" {...register('partnerId')} />
          <TextField label={t('audit.fields.agentId')} size="small" {...register('agentId')} />
          <TextField label={t('audit.fields.requestId')} size="small" {...register('requestId')} />
          <TextField label={t('audit.fields.sourceIp')} size="small" {...register('sourceIp')} />
          <TextField label={t('audit.fields.entityId')} size="small" {...register('entityId')} />
          <TextField
            label={t('audit.fields.to')}
            size="small"
            type="datetime-local"
            {...register('to')}
          />
        </>
      }
    />
  );
}

function CompactAuditResults({
  items,
  language,
}: {
  readonly items: readonly AuditLog[];
  readonly language: TimeLanguage;
}) {
  const { t } = useTranslation();
  return (
    <Stack
      aria-label={t('audit.compact')}
      component="ul"
      spacing={1}
      sx={{ listStyle: 'none', m: 0, p: 0 }}
    >
      {items.map((item) => (
        <Paper component="li" key={item.id} sx={{ p: 2 }} variant="outlined">
          <Stack spacing={0.75}>
            <Link to={`${paths.audit}/${encodeURIComponent(item.id)}`}>{item.id}</Link>
            <Typography variant="body2">{item.action}</Typography>
            <Typography variant="body2">
              {item.entityType} · {item.entityId ?? t('common.notAvailable')}
            </Typography>
            <Typography color="text.secondary" variant="caption">
              {formatTimestamp(item.occurredAt, 'Asia/Bangkok', language).display}
            </Typography>
          </Stack>
        </Paper>
      ))}
    </Stack>
  );
}

function TableRegion({
  children,
  label,
}: {
  readonly children: ReactNode;
  readonly label: string;
}) {
  return (
    <Stack
      aria-label={label}
      role="region"
      tabIndex={0}
      sx={{
        overflowX: 'auto',
        '& table': { borderCollapse: 'collapse', minWidth: 900, width: '100%' },
        '& th, & td': { borderBottom: 1, borderColor: 'divider', p: 1, textAlign: 'left' },
      }}
    >
      {children}
    </Stack>
  );
}

function CursorActions({
  hasNext,
  onNext,
  onPrevious,
  page,
}: {
  readonly hasNext: boolean;
  readonly onNext: () => void;
  readonly onPrevious: () => void;
  readonly page: number;
}) {
  const { t } = useTranslation();
  return (
    <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
      <Button disabled={page === 0} onClick={onPrevious}>
        {t('audit.pagination.previous')}
      </Button>
      <Button disabled={!hasNext} onClick={onNext}>
        {t('audit.pagination.next')}
      </Button>
    </Stack>
  );
}

function initialQuery(params: URLSearchParams): AuditLogQuery {
  return {
    ...defaultAuditLogQuery,
    filters: {
      principalActorId: validUuid(params.get('principalActorId')),
      principalRole: allow(params.get('principalRole'), roleOptions),
      partnerId: validUuid(params.get('partnerId')),
      agentId: validUuid(params.get('agentId')),
      requestId: (params.get('requestId') ?? '').trim(),
      sourceIp: (params.get('sourceIp') ?? '').trim(),
      from: validTime(params.get('from')),
      to: validTime(params.get('to')),
      action: normalizeCode(params.get('action') ?? ''),
      entityType: normalizeCode(params.get('entityType') ?? ''),
      entityId: validUuid(params.get('entityId')),
    },
  };
}

function allow<const Value extends string>(
  value: string | null,
  values: readonly Value[],
): Value | '' {
  return value !== null && values.includes(value as Value) ? (value as Value) : '';
}

function normalizeCode(value: string): string {
  const code = value.trim().toUpperCase();
  return /^[A-Z][A-Z0-9_]{1,99}$/u.test(code) ? code : '';
}

function validTime(value: string | null): string {
  if (
    value === null ||
    !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,6})?)?(?:Z|[+-]\d{2}:\d{2})?$/u.test(value)
  )
    return '';
  try {
    formatTimestamp(bangkokWallTimeToRfc3339(value));
    return value;
  } catch {
    return '';
  }
}

function validUuid(value: string | null): string {
  return value !== null && value.trim().length > 0 ? value.trim() : '';
}

export function toAuditPageError(
  error: unknown,
  fallback: string,
  knownMessages: Readonly<Record<string, string>> = {},
): Pick<AppError, 'message' | 'requestId'> | null {
  if (error === null || error === undefined) return null;
  const candidate = typeof error === 'object' && error !== null ? (error as Partial<AppError>) : {};
  return {
    message:
      typeof candidate.code === 'string' ? (knownMessages[candidate.code] ?? fallback) : fallback,
    requestId: typeof candidate.requestId === 'string' ? candidate.requestId : null,
  };
}
